/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import { atom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { atomFamily } from 'jotai-family';
import type { Feature } from 'geojson';
import type { InitialViewStateProps, ViewStateProps } from './deckgl-types';

export const initialViewState: InitialViewStateProps = {
  altitude: 1.5,
  bearing: 0,
  latitude: 39.446888593720935,
  longitude: -95.65494071225665,
  maxZoom: 18,
  minPitch: 0,
  maxPitch: 85,
  minZoom: 0,
  pitch: 0,
  zoom: 3.4944034428445687,
};

export const viewStateAtom = atom<ViewStateProps & { width?: number; height?: number }>(
  initialViewState
);

export const debouncedViewStateAtom = atom<ViewStateProps & { width?: number; height?: number }>(
  initialViewState
);

/**
 * Whether the camera carries a canvas size yet, which it does once deck has
 * measured the map. Fitting bounds needs it, so a data arrival that frames its
 * extent waits for it instead of racing the first frame. Derived with
 * selectAtom so subscribers re-render when the answer changes, not on every
 * camera move.
 */
export const cameraSizedAtom = selectAtom(
  viewStateAtom,
  camera => camera.width != null && camera.height != null
);

/**
 * True until the camera has been placed for the URL the app was opened on. A
 * `?bbox=` deep link is the view its sender saw, so Map fits it; an incident
 * page opened without one frames the incident's extent instead and clears
 * this itself; any other route without one keeps the national default. While
 * it holds, Map does not publish the camera to the URL, so a deep link's bbox
 * is not overwritten before it is honoured.
 */
export const mapFirstLoadAtom = atom(true);

/** Milliseconds of camera stillness after which the view state counts as settled. */
export const VIEW_STATE_SETTLE_MS = 400;

/**
 * Milliseconds of camera stillness before Map publishes the viewport to the
 * URL's `?bbox=` and to debouncedViewStateAtom.
 */
export const VIEW_STATE_PUBLISH_MS = 1000;

/**
 * The camera once it has held still for VIEW_STATE_SETTLE_MS, and null until it
 * first does. Written by the Map component, which owns the live view state.
 *
 * Read by layers that pay for a fetch when the camera moves: the null start is
 * what distinguishes "the camera has not stopped yet" from "the camera is at
 * the initial national view", so a route that flies to an incident extent on
 * mount only ever requests data for where it lands. debouncedViewStateAtom
 * cannot express that -- it starts at the initial camera.
 */
export const settledViewStateAtom = atom<
  (ViewStateProps & { width?: number; height?: number }) | null
>(null);

/**
 * True while the user is interacting with the map (drag/pan/zoom/rotate) or a
 * camera transition is running. Set from DeckGL's onInteractionStateChange so
 * layers can defer work until the gesture ends instead of debouncing on time.
 */
export const mapInteractingAtom = atom<boolean>(false);

/** What a map click picked: the stacked features under the pointer. */
export type ClickInfo = {
  /** Where on the map the click landed; the popup stays attached to it. */
  coordinate: [number, number, number] | [number, number];
  objects: {
    layer: { id: string };
    /** deck.gl GeoJsonLayer picked objects are GeoJSON Features. */
    object: Feature;
  }[];
};

/**
 * The open feature popup's pick, or null while none is open. Written by Map
 * from deck's pick; cleared when the user dismisses the popup or clicks empty
 * map, and by any camera flight started away from the map (cameraFlightAtom),
 * which moves the map out from under the feature the popup describes.
 */
export const clickInfoAtom = atom<ClickInfo | null>(null);

/** The column the 3D layers extrude by, chosen from a legend's attribute list. */
export const selectedElevationKeyAtom = atom<string>('pop');

export const selectedElevationScaleAtom = atom<number>(1);

export const visualModeAtom = atom<'2d' | '3d'>('2d');

/**
 * Visibility of the overlays the app switches itself: 'structures' (the USA
 * Structures menu; the layer list's own eye for that layer applies on top) and
 * 'county-highlights' (the county chart's highlight toggle).
 */
export const layerVisibility = atomFamily((params: string) =>
  atom(params === 'county-highlights' ? false : true)
);

export const mapTitle = atom('Map');

const selectedDateBaseAtom = atom<Date | null>(new Date());

/**
 * True once the user has navigated to a point in time (slider commit, date
 * pick, chart-point click, alert click). Cleared by TimeSlider when the user
 * navigates back to today's newest snapshot, returning the map to live data.
 */
export const timeNavigatedAtom = atom<boolean>(false);

/**
 * Selecting a time is an act of time navigation, so every write also raises
 * timeNavigatedAtom -- callers that represent "back to live" (TimeSlider)
 * clear it explicitly afterwards.
 */
export const selectedDateAtom = atom(
  get => get(selectedDateBaseAtom),
  (_get, set, date: Date | null) => {
    set(selectedDateBaseAtom, date);
    set(timeNavigatedAtom, true);
  }
);

/** When true, the map overlays every snapshot of the selected day at once. */
export const daySummaryModeAtom = atom<boolean>(false);

/**
 * True when the user is looking at a historical view: the day-summary overlay,
 * or a navigated-to point in time. Decides which layers render -- the current
 * tables (live) or the _history tables (historical) -- so the two never draw
 * on top of each other.
 */
export const viewingHistoricalAtom = atom<boolean>(
  get => get(daySummaryModeAtom) || get(timeNavigatedAtom)
);

export type ExportLegendItem = { label: string; color: string };
export type ExportLegendGroup = {
  title: string;
  items: ExportLegendItem[];
  model?: string;
  updatedOn?: string;
};
export const exportLegendDataAtom = atom<ExportLegendGroup[]>([]);

/** When true, components render with solid backgrounds for export capture */
export const exportModeAtom = atom<boolean>(false);

/** Registry of chart canvas elements for PDF export */
export const exportChartCanvasAtom = atom<Record<string, HTMLCanvasElement | null>>({});

/** Ref to ExposureStats container for html2canvas capture */
export const exportStatsContainerRefAtom = atom<HTMLDivElement | null>(null);

/** Current stats page context */
export const statsPageContextAtom = atom<'home' | 'incident' | null>(null);
